<template>
  <div class="success-ordered-message">
    <div class="background" :style="image">
      <router-link to="/orders" replace>
        <BackstageIcon name="close" class="btn-close" data-cy="popup-close-button" />
      </router-link>
      <div class="popup-container" data-cy="success-order-popup">
        <BackstageIcon name="success" class="icon" data-cy="success-icon" />
        <div>
          <h4 class="title" data-cy="success-title">
            {{ successTitle }}
          </h4>
        </div>
        <p class="info" data-cy="success-message">
          {{ successMessage }}
        </p>
        <router-link to="/orders" replace>
          <BackstageButton
            class="confirm-button"
            full-width
            data-cy="view-orders-button-cnt"
            data-cy-button="view-orders-button">
            {{ $t('successOrderedMessage.okay') }}
          </BackstageButton>
        </router-link>
        <div v-if="loading" class="loader small-loader"></div>
        <div v-else-if="isAllowedToEditOrderForDealer" class="order-edit-controllers">
          <div data-cy="edit-order-action" class="edit-order-actions" @click="redirectToEditOrder">
            <BackstageIcon name="edit" class="edit-order-icon" />
            <p>{{ $t('successOrderedMessage.edit_order') }}</p>
          </div>
        </div>
        <p v-if="!loading && showOrderEditInfoText" class="editing-info-text">
          {{ $t('successOrderedMessage.editing_order_info') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { dealerOrderEditTimeLimit } from '@/constants/order-update.constants';
import { OrderEditType } from '@/constants/orderEditType';
import { localeMixins } from '@/mixins/locale.mixins';
import { ordersMixins } from '@/mixins/orders.mixins';
import { AddEditOrderBreadcrumbSteps } from '@/models/add-edit-order-breadcrumb-steps.enum';
import { OrderAction } from '@/models/order-action.enum';
import type { Order } from '@/models/order.model';
import { RouteNames } from '@/router';
import { OrdersService } from '@/services/orders.service';
import { calculateRemainingTime } from '@/utils/timeUtils';
import { OrderType } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent, ref, type Ref } from 'vue';
import { useUserStore } from '../stores/user';

export default defineComponent({
  name: 'success-ordered-message',
  components: {
    BackstageIcon,
    BackstageButton,
  },
  props: {
    orderNumber: String,
    orderType: String,
  },
  setup() {
    const { userProfile, isDealer } = useUserStore();
    const { selectedAccountId, selectedAccount } = storeToRefs(useUserStore());
    const orderService = new OrdersService();
    const loading = ref(false);
    const hasOrderEditWindowExpired = ref(false);
    const timeoutHandler = ref<NodeJS.Timeout | undefined>(undefined);

    const order: Ref<Order | undefined> = ref(undefined);

    return {
      email: userProfile.profile?.email,
      userProfile,
      isDealer,
      selectedAccountId,
      selectedAccount,
      orderService,
      hasOrderEditWindowExpired,
      timeoutHandler,
      loading,
      order,
    };
  },
  data() {
    return {
      image: {
        backgroundImage: `url(${this.$t('successOrderedMessage.image_url')})`,
      },
    };
  },
  computed: {
    isOrderCreation(): boolean {
      return this.getOrderAction() === OrderAction.Create;
    },
    successTitle(): string {
      const title = localeMixins.getLocaleTextByOrderType('viewOrders.order_type.', this.orderType as OrderType);

      return this.isOrderCreation
        ? `${this.$t(title)} ${this.$t('successOrderedMessage.submitted')}.`
        : `${this.$t(title)} ${this.orderNumber} ${this.$t('successOrderedMessage.submitted')}.`;
    },
    successMessage(): string {
      return this.isOrderCreation
        ? `${this.$t('successOrderedMessage.will_receive')} ${this.email}. ${this.$t(
            'successOrderedMessage.track_order',
          )}.`
        : `${this.$t('successOrderedMessage.update_success_message')}`;
    },
    isAllowedToEditOrderForDealer(): boolean {
      if (!this.order) {
        return false;
      }

      return (
        !this.isMultiplePreOrder && // We don't want to show the edit button for the multiple pre-orders.
        this.isDealer() &&
        this.isOrderCreation &&
        this.isOrderEditableForDealer(this.order, this.userProfile?.user) &&
        !this.hasOrderEditWindowExpired
      );
    },
    showOrderEditInfoText(): boolean {
      if (!this.isDealer() || this.hasOrderEditWindowExpired || !this.isOrderCreation) {
        return false;
      }

      return this.isMultiplePreOrder || this.isAllowedToEditOrderForDealer;
    },
    isMultiplePreOrder(): boolean {
      return this.orderType === OrderType.MultiplePreOrder;
    },
  },
  async mounted() {
    if (this.isDealer() && this.isOrderCreation && !this.isMultiplePreOrder) {
      await this.fetchOrder();
    }
  },
  unmounted() {
    if (this.timeoutHandler) {
      clearTimeout(this.timeoutHandler);
    }
  },
  methods: {
    ...ordersMixins,
    async fetchOrder() {
      if (!this.orderNumber || !this.selectedAccountId) {
        return;
      }

      try {
        this.loading = true;
        const response = await this.orderService.get(this.orderNumber, this.selectedAccountId);
        if (response.errors.length > 0) {
          this.loading = false;

          return;
        }

        if (!response.model) {
          this.loading = false;

          return;
        }

        this.order = response.model;

        this.calculateTimeout(response.model);

        this.loading = false;
      } catch (error) {
        this.order = undefined;
      } finally {
        this.loading = false;
      }
    },
    redirectToEditOrder() {
      this.$router.push({
        name: RouteNames.EditOrder,
        params: {
          id: this.orderNumber,
          editType: OrderEditType.Order,
          step: AddEditOrderBreadcrumbSteps.productsModelsSelection,
        },
      });
    },
    calculateTimeout(order: Order) {
      const timeRemaining = calculateRemainingTime(order.date, dealerOrderEditTimeLimit);

      if (timeRemaining <= 0) {
        this.hasOrderEditWindowExpired = true;
      } else {
        this.timeoutHandler = setTimeout(() => {
          this.hasOrderEditWindowExpired = true;
        }, timeRemaining);
      }
    },
    getOrderAction(): OrderAction {
      return this.$route.query.orderAction as OrderAction;
    },
  },
});
</script>

<style lang="scss" scoped>
.success-ordered-message {
  max-width: 87.5rem;
  margin: 0 auto;
  width: 100%;
  height: fit-content;

  .background {
    position: relative;
    top: 0;
    background-position: top center;
    background-size: cover;
    background-repeat: no-repeat;
    height: 40.375rem;
    .popup-container {
      position: relative;
      top: 28%;
      max-width: 25.3125rem;
      height: fit-content;
      margin: auto;
      background-color: var(--primary-bg-color);
      border-radius: 0.25rem;
      padding: 2rem 1.25rem;

      .icon {
        width: 3rem;
        height: 3rem;
        margin: auto;
      }

      .title {
        margin: 2.5rem 0 0.25rem;
        font-weight: 500;
        font-size: 1.25rem;
        line-height: 150%;
      }

      .info {
        font-weight: 400;
        font-size: 1rem;
        line-height: 150%;
        color: var(--primary-text-remark-color);
        width: 80%;
        margin: auto;
      }

      .order-edit-controllers {
        position: relative;
        margin: auto;
        max-width: 80%;
      }

      .edit-order-actions {
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        gap: 0.2rem;
        font-size: 0.875rem;
        height: fit-content;
        color: var(--primary-text-color);

        p {
          text-decoration: underline;
          font-size: 1rem;
          margin: 0;
          padding: 0;
        }
      }

      :deep(.confirm-button) {
        margin: 0.5rem 0;

        button {
          font-size: 1.1rem;
          height: 64px;
        }
      }

      .edit-order-icon {
        width: 18px;
        height: 18px;
      }

      .editing-info-text {
        font-size: 0.8rem;
        color: var(--primary-text-remark-color);
        text-align: center;
        margin-top: 1rem;
      }
    }
    .btn-close {
      position: absolute;
      width: 2.625rem;
      height: 2.625rem;
      top: 1rem;
      right: 1rem;
      opacity: 1;
      background-color: var(--primary-bg-color);
      border-radius: 50%;

      &:hover {
        background-color: var(--primary-bg-color);
      }
      &:before,
      &:after {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        margin: auto;
        content: ' ';
        height: 1.125rem;
        width: 0.0625rem;
        background-color: var(--secondary-bg-color);
      }

      &:before {
        transform: rotate(45deg);
      }

      &:after {
        transform: rotate(-45deg);
      }
    }
  }
}

.small-loader {
  height: 30px;
}
</style>
